import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://hvkurz.de';

/**
 * SEORedirect — SPA soft-301 for permanent URL moves.
 *
 * Emergent's ingress does not currently support real HTTP 301 responses for
 * non-/api paths. This component delivers the strongest SEO signals a client
 * can emit, without letting React Router overwrite the Helmet tags:
 *
 *   1. <link rel="canonical" href="ABSOLUTE-TARGET"> — consolidates ranking &
 *      link equity onto the target URL (Google's primary signal for URL moves).
 *   2. <meta name="robots" content="noindex, follow"> — drops the old URL from
 *      the index while still following the redirect for link-equity transfer.
 *   3. <meta http-equiv="refresh" content="0; url=…"> — instant browser-native
 *      redirect (Google treats this as a permanent redirect when combined
 *      with the canonical, see google.com/search/docs).
 *   4. useEffect → window.location.replace(to) — belt-and-braces JS fallback
 *      for browsers/bots that need explicit navigation.
 *
 * Deliberately NOT using <Navigate>: it fires during render and immediately
 * mounts the target route, overwriting our Helmet tags before crawlers can
 * observe them.
 *
 * The visible <noscript> fallback keeps the page usable for JS-disabled bots.
 */
export default function SEORedirect({ to }) {
  const absoluteTarget = to.startsWith('http') ? to : `${SITE_URL}${to}`;

  useEffect(() => {
    // Belt-and-braces JS redirect (in addition to meta refresh)
    window.location.replace(to);
  }, [to]);

  return (
    <>
      <Helmet>
        <link rel="canonical" href={absoluteTarget} />
        <meta name="robots" content="noindex, follow" />
        <meta httpEquiv="refresh" content={`0; url=${to}`} />
        <title>{`Weiterleitung zu ${absoluteTarget}`}</title>
      </Helmet>
      <main
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
        data-testid="seo-redirect-page"
      >
        <div>
          <p style={{ fontSize: '1rem', color: '#555' }}>
            Diese Seite wurde dauerhaft verschoben.
          </p>
          <p style={{ fontSize: '0.95rem', marginTop: '0.75rem' }}>
            Bitte fortfahren zu <a href={to} style={{ color: '#c8a94b', textDecoration: 'underline' }}>{absoluteTarget}</a>
          </p>
        </div>
      </main>
    </>
  );
}
